import Card from '@/components/elements/Card/Card';
import { Button, Fab } from '@/components/elements/forms/buttons';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import Rating from '@/components/elements/redesign/Rating/Rating';
import Icon from '@/components/icons/Icon';
import EmptyState from '@/features/ProfilePages/components/EmptyState';
import { promiseWrapper } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { __, _s } from '@/locale';
import {
  getPlaceRatingSummaryResponseSchema,
  getPlaceReviewsResponseSchema,
  Review,
  reviewsServices,
} from '@/services';
import * as Sentry from '@sentry/react';
import { useEffect, useReducer } from 'react';
import { toast } from 'react-toastify';
import ReviewCard from '../../../components/elements/Card/ReviewCard';
import { getAuthorInitials } from '../helpers/review';
import ReviewModal from '../modules/ReviewModal';

const baseTranslationKey = 'features.ProfilePages.components.Reviews';

type UseReviewsProps = {
  placeId: string;
  tooltipTargetRef: React.RefObject<HTMLDivElement>;
};

type ReviewState = {
  reviews: Review[];
  totalPages: number;
  totalReviews: number;
  rating: { score: number; count: number };
  isLoading: boolean;
  currentPage: number;
  showAddReviewModal: boolean;
};

type ReviewAction =
  | { type: 'SET_REVIEWS'; payload: { reviews?: Review[]; totalPages?: number; totalReviews?: number } }
  | { type: 'SET_RATING'; payload: { score: number; count: number } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'SET_SHOW_ADD_REVIEW_MODAL'; payload: boolean };

const reviewsReducer = (state: ReviewState, action: ReviewAction) => {
  switch (action.type) {
    case 'SET_REVIEWS':
      return { ...state, isLoading: false, ...action.payload };
    case 'SET_RATING':
      return { ...state, rating: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload };
    case 'SET_SHOW_ADD_REVIEW_MODAL':
      return { ...state, showAddReviewModal: action.payload };
    default:
      return state;
  }
};

const useReviews = ({ placeId, tooltipTargetRef }: UseReviewsProps) => {
  const { isMobileView } = useMobileView();
  const [state, dispatch] = useReducer(reviewsReducer, {
    reviews: [],
    totalPages: 0,
    totalReviews: 0,
    rating: { score: 0, count: 0 },
    isLoading: false,
    currentPage: 1,
    showAddReviewModal: false,
  });

  const haveReviews = Boolean(state.reviews?.length);

  useEffect(() => {
    fetchReviews();
    getReviewsAverage();
  }, [state.currentPage, placeId]);

  const fetchReviews = async () => {
    let timeoutId: NodeJS.Timeout;

    timeoutId = setTimeout(() => {
      dispatch({ type: 'SET_LOADING', payload: true });
    }, 1000);

    const { data, error } = await promiseWrapper(
      reviewsServices.getPlaceReviews(Number(placeId), state.currentPage, 4),
    );

    const validData = getPlaceReviewsResponseSchema.safeParse(data);

    if (error || !validData.success) {
      Sentry.captureException(error || validData.error);
      return;
    }

    dispatch({
      type: 'SET_REVIEWS',
      payload: validData.data,
    });

    clearTimeout(timeoutId);
  };

  const getReviewsAverage = async () => {
    const { data } = await promiseWrapper(reviewsServices.getPlaceRatingSummary(Number(placeId)));

    const validData = getPlaceRatingSummaryResponseSchema.safeParse(data);

    if (!validData.success) {
      Sentry.captureException(validData.error);
      return;
    }

    const { averageScore, ratingCount } = validData.data;

    dispatch({ type: 'SET_RATING', payload: { score: averageScore, count: ratingCount } });
  };

  const handleClickAddReview = () => {
    dispatch({ type: 'SET_SHOW_ADD_REVIEW_MODAL', payload: !state.showAddReviewModal });
  };

  const handleCloseAddReviewModal = () => {
    dispatch({ type: 'SET_SHOW_ADD_REVIEW_MODAL', payload: false });
  };

  const handleClickNextPage = () => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: state.currentPage + 1 });
  };

  const handleClickPreviousPage = () => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: state.currentPage - 1 });
  };

  const handleChangeReviews = () => {
    fetchReviews();
    getReviewsAverage();
  };

  const handleDeleteReview = async (id: string) => {
    let timeoutId: NodeJS.Timeout;

    try {
      timeoutId = setTimeout(() => {
        dispatch({ type: 'SET_LOADING', payload: true });
      }, 1000);

      const { error } = await promiseWrapper(reviewsServices.deleteExternal(id));
      if (!error) {
        dispatch({ type: 'SET_SHOW_ADD_REVIEW_MODAL', payload: false });
        toast(({ closeToast }) => (
          <Snackbar type="success" label={__(`${baseTranslationKey}.alert.success`)} onClose={closeToast} />
        ));
        fetchReviews();
        getReviewsAverage();
      } else {
        toast(({ closeToast }) => (
          <Snackbar type="danger" label={__(`${baseTranslationKey}.alert.error`)} onClose={closeToast} />
        ));
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      clearTimeout(timeoutId);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  return {
    render: (
      <>
        <Card size={isMobileView ? 'lg' : 'xl'}>
          {state.isLoading && <LoadingPlaceHolder />}
          {!state.isLoading && (
            <div className={`${isMobileView ? 'gap-xl' : 'gap-2xl'} flex flex-col`}>
              <div className={`flex  ${haveReviews && isMobileView ? 'gap-xl flex-col' : 'justify-between'}`}>
                <Rating
                  ratingCount={state.rating.count}
                  avgRating={state.rating.score}
                  reviewCount={state.totalReviews}
                  className={`${isMobileView ? 'text-h-l' : 'text-h-xl'}`}
                  size="lg"
                />
                {(!isMobileView || haveReviews) && (
                  <Button
                    size="md"
                    variant={haveReviews ? 'primary' : 'secondary'}
                    label={_s(`${baseTranslationKey}.cta`)}
                    iconNoFilter
                    onClick={handleClickAddReview}
                    rightIcon={<Icon variant="pen" color={haveReviews ? 'fg_primary_inverse' : 'fg_primary'} />}
                  />
                )}
              </div>
              {!haveReviews && (
                <EmptyState
                  body={_s(`${baseTranslationKey}.emptyState.body`)}
                  title={_s(`${baseTranslationKey}.emptyState.title`)}
                  icon={<img src="/images/illustrations/empty.svg" alt="" />}
                />
              )}
              {haveReviews && (
                <div className="gap-md flex flex-col">
                  {state.reviews.map((review, index) => {
                    const initials = getAuthorInitials(review.review.author);
                    return (
                      <ReviewCard
                        tooltipTargetRef={tooltipTargetRef}
                        verified={review.verified}
                        key={index}
                        placeId={placeId}
                        reviewId={String(review.id)}
                        onEdit={review.canEdit ? handleChangeReviews : undefined}
                        onDelete={handleDeleteReview}
                        companyReply={review.companyReply ? { ...review.companyReply } : undefined}
                        review={{ ...review.review, initials }}
                      />
                    );
                  })}
                </div>
              )}
              {haveReviews && state.totalReviews > 4 && (
                <div className="flex justify-between">
                  <Fab
                    icon={
                      <Icon variant="chevron-left" color={state.currentPage === 1 ? 'fg_disabled' : 'fg_primary'} />
                    }
                    onClick={handleClickPreviousPage}
                    size="sm"
                    disabled={state.currentPage === 1}
                    variant="secondary"
                  />
                  <div className="text-fg_secondary">
                    <span>{_s(`${baseTranslationKey}.pagination.page`)}</span> <span>{state.currentPage}</span>{' '}
                    <span>{_s(`${baseTranslationKey}.pagination.of`)}</span>{' '}
                    <span>{state.totalPages === 0 ? 1 : state.totalPages}</span>
                  </div>
                  <Fab
                    icon={
                      <Icon
                        variant="chevron-right"
                        color={
                          state.currentPage === state.totalPages || state.totalPages === 0
                            ? 'fg_disabled'
                            : 'fg_primary'
                        }
                      />
                    }
                    onClick={handleClickNextPage}
                    disabled={state.currentPage === state.totalPages || state.totalPages === 0}
                    size="sm"
                    variant="secondary"
                  />
                </div>
              )}
              {isMobileView && !haveReviews && (
                <Button
                  size="md"
                  variant="secondary"
                  onClick={handleClickAddReview}
                  iconNoFilter
                  label={_s(`${baseTranslationKey}.cta`)}
                  rightIcon={<Icon variant="pen" color="fg_primary" />}
                />
              )}
            </div>
          )}
        </Card>
        {state.showAddReviewModal && (
          <ReviewModal onClose={handleCloseAddReviewModal} placeId={placeId} onChangeReviews={handleChangeReviews} />
        )}
      </>
    ),
    getTotalReviews: state.totalReviews,
    rating: state.rating.score,
  };
};

export default useReviews;
